import { cn } from "@/lib/utils";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link } from "@tanstack/react-router";
import { Sparkles, TriangleAlert } from "lucide-react";

interface MarginCustomer {
	customerId: string;
	customerName: string;
	revenue: number;
	margin: number;
	invoiceCount: number;
}

interface MarginLeaderListProps {
	title: string;
	customers: MarginCustomer[];
	/** Flip to a warning row style when margin is below the threshold.
	 * Drives the "Review pricing — low margin" callout on the bottom-customers
	 * card. Undefined disables the warning style entirely. */
	warnBelowPct?: number;
}

/**
 * Replaces the old dashboard's tabular MarginTable with a vertically stacked
 * list. Each row shows the customer, margin % (color-graded by tier), and a
 * one-line note. Low-margin customers (below `warnBelowPct`) are flagged
 * with an amber warning icon + "Review pricing" note.
 */
export function MarginLeaderList({ title, customers, warnBelowPct }: MarginLeaderListProps) {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid="margin-leader-list">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<div className="flex items-center gap-2">
					<Sparkles className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
					<h2 className="text-sm font-semibold text-[#1e1e20]">{title}</h2>
				</div>
				<span className="text-[11px] text-[#9ca3af]">All time</span>
			</div>
			{customers.length === 0 ? (
				<div className="px-6 py-10 text-center text-sm text-[#6b7280]">
					No invoiced customers yet.
				</div>
			) : (
				<div>
					{customers.map((c, idx) => {
						const marginPct = c.revenue > 0 ? Math.round((c.margin / c.revenue) * 100) : 0;
						const isWarn = warnBelowPct !== undefined && marginPct < warnBelowPct;
						const tierColor = isWarn
							? "text-[#d97706]"
							: marginPct >= 45
								? "text-[#16a34a]"
								: marginPct >= 30
									? "text-[#1e1e20]"
									: "text-[#6b7280]";
						return (
							<div
								key={c.customerId}
								className={cn("px-6 py-2.5", idx > 0 && "border-t border-[#e5e7eb]")}
							>
								<div className="flex items-center gap-2">
									{isWarn && (
										<TriangleAlert
											className="h-3.5 w-3.5 shrink-0 text-[#d97706]"
											strokeWidth={2}
										/>
									)}
									<Link
										to="/customers/$customerId"
										params={{ customerId: c.customerId }}
										className="flex-1 truncate text-[13px] font-medium text-[#1e1e20] hover:text-[#4f46e5] hover:underline"
									>
										{c.customerName}
									</Link>
									<span className={cn("tabular-nums text-[13px] font-semibold", tierColor)}>
										{marginPct}%
									</span>
								</div>
								<div
									className={cn(
										"mt-0.5 pl-[18px] text-[11.5px]",
										isWarn ? "text-[#d97706]" : "text-[#6b7280]",
									)}
								>
									{isWarn
										? "Review pricing — low margin"
										: `${formatDollars(c.margin)} across ${c.invoiceCount} ${c.invoiceCount === 1 ? "invoice" : "invoices"}`}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
